import { AxisValueFormatterContext } from '@mui/x-charts/models';

export const resolveChartLocale = (locale: string = 'en-GB'): string => locale.replace(/_/g, '-').replace(/^vn(?=-|$)/i, 'vi');

// Ticks below this magnitude show full numbers; larger axes use compact notation such as 240k.
const COMPACT_FROM = 10_000;

// The same 1, 2, 5 × 10ⁿ step d3 chooses for its ticks.
const tickStep = (start: number, stop: number, count: number): number => {
  const rawStep = Math.abs(stop - start) / Math.max(count, 1);
  if (!Number.isFinite(rawStep) || rawStep === 0) return 0;
  const power = 10 ** Math.floor(Math.log10(rawStep));
  const error = rawStep / power;
  return power * (error >= Math.sqrt(50) ? 10 : error >= Math.sqrt(10) ? 5 : error >= Math.sqrt(2) ? 2 : 1);
};

// Enough decimals that neighbouring ticks never round to the same label.
const fractionDigitsFor = (step: number): number => (step > 0 ? Math.max(0, -Math.floor(Math.log10(step) + 1e-9)) : 0);

export const createAxisTickFormatter = (locale: string = 'en-GB'): ((value: number, context: AxisValueFormatterContext) => string) => {
  const resolvedLocale = resolveChartLocale(locale);
  const numberFormatter = new Intl.NumberFormat(resolvedLocale);
  const tickFormatters = new Map<string, Intl.NumberFormat>();
  const tickFormatter = (compact: boolean, fractionDigits: number) => {
    const key = `${compact}-${fractionDigits}`;
    const cached = tickFormatters.get(key);
    if (cached) return cached;
    const formatter = new Intl.NumberFormat(
      resolvedLocale,
      compact
        ? { notation: 'compact', maximumFractionDigits: fractionDigits }
        : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }
    );
    tickFormatters.set(key, formatter);
    return formatter;
  };

  return (value, context) => {
    if (context.location !== 'tick') return numberFormatter.format(value);
    // d3 leaves some log-scale ticks unlabelled on purpose.
    if (context.defaultTickLabel === '') return '';

    const [start, stop] = context.scale.domain().map(Number);
    const step = tickStep(start, stop, context.tickNumber ?? 5);
    const largest = Math.max(Math.abs(start), Math.abs(stop));
    if (largest < COMPACT_FROM) return tickFormatter(false, fractionDigitsFor(step)).format(value);

    const compactUnit = 10 ** (3 * Math.floor(Math.log10(largest) / 3));
    return tickFormatter(true, fractionDigitsFor(step / compactUnit)).format(value);
  };
};
